import React, { createContext, useContext, useMemo, useState } from "react";
import { DocumentCurrencyConfigView } from "@/Contracts";

export interface DocumentCurrencyContextValue {
	/** Currency code the document is authored in (null = company primary). */
	currency: string | null;
	/**
	 * Exchange rate captured at status freeze (Sent/Won/Lost/Signed/SignatureInProgress).
	 * Null while the document is still editable — callers should fall back to the
	 * live rate from useCurrencyConfig.
	 */
	frozenRate: number | null;
	/** True once the document's currency config is frozen. */
	isFrozen: boolean;
	companyCurrency: string | null;
	skipLiveConfigFetch: boolean;
	/**
	 * Whether the editor is currently showing quote-currency amounts. Default
	 * OFF (company-currency view) per spec. Shared across every toggle and price
	 * cell inside this provider so flipping any one toggle updates the whole doc.
	 */
	isQuoteView: boolean;
	setQuoteView: (next: boolean) => void;
}

const DocumentCurrencyContext =
	createContext<DocumentCurrencyContextValue | null>(null);

interface DocumentCurrencyProviderProps {
	config: DocumentCurrencyConfigView | null;
	companyCurrency?: string | null;
	skipLiveConfigFetch?: boolean;
	children: React.ReactNode;
}

export const DocumentCurrencyProvider: React.FC<
	DocumentCurrencyProviderProps
> = ({
	config,
	companyCurrency = null,
	skipLiveConfigFetch = false,
	children
}) => {
	const [isQuoteView, setQuoteView] = useState(false);

	const value = useMemo<DocumentCurrencyContextValue>(() => {
		if (!config) {
			return {
				currency: null,
				frozenRate: null,
				isFrozen: false,
				companyCurrency,
				skipLiveConfigFetch,
				isQuoteView,
				setQuoteView
			};
		}
		const frozenRate = config.frozenExchangeRate ?? null;
		return {
			currency: config.currency.code,
			frozenRate,
			isFrozen: frozenRate !== null,
			companyCurrency,
			skipLiveConfigFetch,
			isQuoteView,
			setQuoteView
		};
	}, [config, companyCurrency, skipLiveConfigFetch, isQuoteView]);

	return (
		<DocumentCurrencyContext.Provider value={value}>
			{children}
		</DocumentCurrencyContext.Provider>
	);
};

/**
 * Returns the document's currency context, or null when called outside a
 * <DocumentCurrencyProvider> (e.g. from the catalog page).
 */
export const useDocumentCurrency = (): DocumentCurrencyContextValue | null =>
	useContext(DocumentCurrencyContext);

export default DocumentCurrencyContext;
